// src/pages/courses/CourseList.tsx
"use client"
import type React from "react"
import { useState, useEffect } from "react"
import { useNavigate, Link } from "react-router-dom"
import Button from "../../components/common/Button"
import DashboardLayout from "../../components/layout/DashboardLayout"
import { getAllCourses, softDeleteCourse, permanentDeleteCourse, getAllGroups } from "../../api/courseService"
import Alert from "../../components/common/alert"
import { ExternalLink, Plus, Search, ChevronLeft, ChevronRight, MoreHorizontal, Edit, Trash } from "lucide-react"

interface Course {
  _id: string
  title: string
  thumbnail: string
  createdAt: string | null
  createdBy: {
    _id: string
    name: string
  } | null  
  updatedAt: string | null
  updatedBy: {
    _id: string
    name: string
  } | null
  status: string
  stage: string
  courseType: string
  webinarChannel: string | null
}

const CourseList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [courses, setCourses] = useState<Course[]>([])
  const [loading, setLoading] = useState(true)
  const [alert, setAlert] = useState<{type: 'success' | 'error' | 'info' | 'warning', message: string} | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDeleteCourse, setConfirmDeleteCourse] = useState<Course | null>(null)
  const [deleteTypeCourse, setDeleteTypeCourse] = useState<Course | null>(null)
  const [activeTab, setActiveTab] = useState<"all" | "Published" | "Draft" | "Sold out">("all")
  const [showSoldOutPopup, setShowSoldOutPopup] = useState(false)
  const [groupNames, setGroupNames] = useState<Record<string, string>>({})

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  useEffect(() => {
    const fetchGroups = async () => {
      const response = await getAllGroups()
      if (response.status === 200 && response.data) {
        const map: Record<string, string> = {}
        response.data.forEach((g) => { map[g.id] = g.name })
        setGroupNames(map)
      }
    }
    fetchGroups()
  }, [])

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await getAllCourses();
        console.log("API Response:", response);
        if (response.status === 200 && response.data) {
          const transformedCourses = response.data.map((course: any) => ({
            _id: course._id,
            title: course.title,
            createdAt: course.createdAt,
            createdBy: course.created_by
              ? {
                  _id: course.created_by._id,
                  name: course.created_by.name,
                }
              : null,
            updatedAt: course.updatedAt,
            updatedBy: course.updatedBy
              ? {
                  _id: course.updatedBy._id,
                  name: course.updatedBy.name,
                }
              : null,  
            status: course.PublishStatus,
            stage: course.creatationStage,
            courseType: course.courseType || 'Course',
            webinarChannel: course.webinarChannel || null
          }));
          setCourses(transformedCourses);
        } else {
          setAlert({
            type: 'error', 
            message: response.message || 'Failed to load courses'
          });
        }
      } catch (error) {
        setAlert({
          type: 'error',
          message: 'Please check your authentication and try again'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm])

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTab = activeTab === "all" || course.status === activeTab
    return matchesSearch && matchesTab
  })

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentCourses = filteredCourses.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredCourses.length / itemsPerPage)

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)

  const pageNumbers = []
  const maxVisiblePages = 5

  if (totalPages <= maxVisiblePages) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i)
  } else {
    pageNumbers.push(1)
    let startPage = Math.max(2, currentPage - 1)
    let endPage = Math.min(totalPages - 1, currentPage + 1)

    if (currentPage <= 2) endPage = 3
    if (currentPage >= totalPages - 1) startPage = totalPages - 2

    if (startPage > 2) pageNumbers.push('...')
    for (let i = startPage; i <= endPage; i++) pageNumbers.push(i)
    if (endPage < totalPages - 1) pageNumbers.push('...')
    pageNumbers.push(totalPages)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Draft": return "text-blue-600 bg-blue-50"
      case "Published": return "text-green-600 bg-green-50"
      case "Sold out": return "text-blue-600 bg-blue-50"
      case "Selling": return "text-green-600 bg-green-50"
      default: return "text-gray-600 bg-gray-50"
    }
  }

  const getTypeColor = (courseType: string) => {
    return courseType === "Webinar" ? "text-purple-600 bg-purple-50" : "text-indigo-600 bg-indigo-50"
  }

  const handleDelete = (course: Course) => {
    // Step 1: ask whether the user wants to delete this course at all
    setConfirmDeleteCourse(course)
  }

  const handleConfirmDelete = () => {
    // User said "Delete" on step 1 — move to step 2 (soft vs permanent)
    const course = confirmDeleteCourse
    setConfirmDeleteCourse(null)
    setDeleteTypeCourse(course)
  }

  const handleSoftDelete = async () => {
    const course = deleteTypeCourse
    if (!course) return
    setDeleteTypeCourse(null)
    setDeletingId(course._id)
    try {
      const userData = localStorage.getItem('userData');
      const userId = userData ? JSON.parse(userData)._id : '';
      const response = await softDeleteCourse(course._id, userId);

      if (response.status === 200) {
        setCourses(courses.filter(c => c._id !== course._id));
        setAlert({
          type: 'success',
          message: 'Course moved to trash successfully'
        });
      } else {
        setAlert({
          type: 'error',
          message: response.message || 'Failed to delete course'
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'An error occurred while deleting the course'
      });
    } finally {
      setDeletingId(null);
    }
  }

  const handlePermanentDelete = async () => {
    const course = deleteTypeCourse
    if (!course) return
    setDeleteTypeCourse(null)
    setDeletingId(course._id)
    try {
      const response = await permanentDeleteCourse(course._id);

      if (response.status === 200) {
        setCourses(courses.filter(c => c._id !== course._id));
        setAlert({
          type: 'success',
          message: 'Course permanently deleted'
        });
      } else {
        setAlert({
          type: 'error',
          message: response.message || 'Failed to permanently delete course'
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'An error occurred while permanently deleting the course'
      });
    } finally {
      setDeletingId(null);
    }
  }

  const handleViewCourse = (courseId: string, status: string) => {
    if (status === "Sold out") {
      setShowSoldOutPopup(true)
    } else {
      navigate(`/courses/${courseId}`)
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    
    <DashboardLayout>
     <div className="w-full min-w-0 mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-4">
      {alert && (
        <Alert 
          type={alert.type} 
          message={alert.message}
          onClose={() => setAlert(null)}
          autoClose={true}
        />
      )}
      
      {showSoldOutPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Course Not Available</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  This course is sold out and is not available for viewing at this time.
                </p>
              </div>
              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setShowSoldOutPopup(false)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: confirm intent to delete */}
      {confirmDeleteCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">Delete Course</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{confirmDeleteCourse.title}"?
                </p>
              </div>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  type="button"
                  onClick={() => setConfirmDeleteCourse(null)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: choose soft delete (trash) vs permanent delete */}
      {deleteTypeCourse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 transform transition-all duration-300 scale-100">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">How do you want to delete this course?</h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  "Move to Trash" can be recovered later. "Delete Permanently" removes the course and all its modules, lessons, certificates, pricing and uploaded files immediately — this <span className="font-semibold text-red-600">cannot be undone</span>.
                </p>
              </div>
              <div className="mt-6 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleSoftDelete}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-gray-700 border border-transparent rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Move to Trash
                </button>
                <button
                  type="button"
                  onClick={handlePermanentDelete}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Delete Permanently
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTypeCourse(null)}
                  className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3 sm:space-y-2">
        {/* Header Section with Better Typography */}
      <div className="bg-white rounded-md shadow-sm border border-gray-200 p-3 mt-2">
          <div className="flex items-center justify-between gap-3 w-full">
             <div className=" text-xl sm:text-2xl md:text-2xl lg:text-3xl font-extrabold text-gray-900 flex items-center gap-2">
                📚 Courses List
              </div>
          <Button
                onClick={() => navigate("/courses/create")}
                className="
                  h-10
                  px-4
                  bg-gradient-to-r from-purple-600 to-blue-600
                  text-white
                  inline-flex items-center justify-center
                  text-sm font-semibold
                  rounded-md
                  shadow-md
                  whitespace-nowrap
                "
              >
                <Plus className="mr-1" size={14} />
                Create
              </Button>


          </div>

          {/* Action Bar */}
          <div className="mt-3">
              <div className="relative w-full">          
                <Search className="absolute left-2 mt-5 transform -translate-y-1/2 text-gray-400" size={12} />
                <input
                  id="search"
                  type="text"
                  placeholder="Search by course name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-transparent transition-all duration-200 bg-gray-50 hover:bg-white hover:shadow-sm"
                />
              </div>
            </div>
          </div>

        {/* Course Table */}
        <div className="bg-white rounded-md shadow-md border border-gray-200">
            {/* Table Header with Status Tabs - always visible so the user can switch tabs even when the current tab has no courses */}
            <div className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 px-3 py-3 border-b border-gray-200">
              <div className=" flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Your Courses</h3>
                  <p className="text-md text-gray-600 mt-1">
                    {filteredCourses.length} of {courses.length} courses shown
                  </p>
                </div>
                
                {/* Status Filter Tabs */}
                <div className="flex space-x-1 bg-white rounded-md p-1 border border-gray-200 shadow-sm">
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-2 py-1 text-md font-medium rounded transition-all duration-200 ${
                      activeTab === "all"
                        ? "bg-purple-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setActiveTab("Published")}
                    className={`px-2 py-1 text-md font-medium rounded transition-all duration-200 ${
                      activeTab === "Published"
                        ? "bg-green-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Published
                  </button>
                  <button
                    onClick={() => setActiveTab("Draft")}
                    className={`px-2 py-1 text-md font-medium rounded transition-all duration-200 ${
                      activeTab === "Draft"
                        ? "bg-blue-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Draft
                  </button>
                  <button
                    onClick={() => setActiveTab("Sold out")}
                    className={`px-2 py-1 text-md font-medium rounded transition-all duration-200 ${
                      activeTab === "Sold out"
                        ? "bg-yellow-500 text-white shadow-sm"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                    }`}
                  >
                    Sold Out
                  </button>
                </div>
              </div>
            </div>

            {filteredCourses.length > 0 ? (
<>
  {/* Mobile Cards View - Show on small screens */}
  <div className="block md:hidden">
    {currentCourses.map((course) => (
      <div
        key={course._id}
        className="border-b border-gray-100 p-3 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-start space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-blue-100 rounded-md flex items-center justify-center flex-shrink-0">
            <span className="text-purple-600 font-bold text-md">
              {course.title.charAt(0).toUpperCase()}
            </span>
          </div>

          <div className="flex-1 min-w-0">
            <div
              onClick={() => handleViewCourse(course._id, course.status)}
              className="font-semibold text-gray-900 hover:text-purple-600 transition-colors block truncate text-md cursor-pointer"
            >
              {course.title}
            </div>

            <div className="mt-1 space-y-1">

              {/* Created At */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Created At:</span>
                <span className="text-gray-900 font-medium">
                  {course.createdAt
                    ? new Date(course.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'N/A'}
                </span>
              </div>

              {/* Created By */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Created By:</span>
                <span className="text-gray-900 font-medium truncate">
                  {course.createdBy?.name || 'N/A'}
                </span>
              </div>

              {/* Type */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Type:</span>
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getTypeColor(course.courseType)}`}
                >
                  {course.courseType === "Webinar"
                    ? `Webinar (${(course.webinarChannel && groupNames[course.webinarChannel]) || '-'})`
                    : "Course"}
                </span>
              </div>

              {/* Status */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Status:</span>
                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getStatusColor(course.status)}`}
                >
                  {course.status}
                </span>
              </div>

              {/* Updated By */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Updated By:</span>
                <span className="text-gray-900 font-medium truncate">
                  {course.updatedBy?.name || 'N/A'}
                </span>
              </div>

              {/* Updated At */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Updated At:</span>
                <span className="text-gray-900 font-medium">
                  {course.updatedAt
                    ? new Date(course.updatedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'N/A'}
                </span>
              </div>

              {/* Stage */}
              <div className="flex items-center gap-4 text-md">
                <span className="text-gray-500">Stage:</span>
                <span className="text-gray-900 font-medium">
                  {course.stage}
                </span>
              </div>

            </div>

            {/* Actions */}
            <div className="mt-2 flex items-center space-x-2">
              <Link
                to={`/courses/edit/${course._id}`}
                className="text-blue-600 hover:text-blue-700"
                title="Edit Course"
              >
                <Edit size={18} />
              </Link>

              <span className="text-gray-300 text-md">•</span>

              <button
                className="text-red-600 hover:text-red-700 disabled:opacity-50"
                title="Delete Course"
                onClick={() => handleDelete(course)}
                disabled={deletingId === course._id}
              >
                {deletingId === course._id ? (
                  <div className="w-2.5 h-2.5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Trash size={18} />
                )}
              </button>
            </div>

          </div>
        </div>
      </div>
    ))}
  </div>
 
               
            
            {/* Desktop Table View - Show on medium and large screens */}
           <div className="hidden md:block w-full min-w-0 overflow-x-auto pb-2">
              <table className="min-w-[1350px] w-full table-auto">
<thead className="bg-gradient-to-r from-gray-50 via-purple-25 to-blue-25 border-b border-gray-200">
  <tr>
    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide min-w-[180px]">
      Course Details
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
      Type
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide whitespace-nowrap">
      Created Date
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide whitespace-nowrap">
      Created By
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
      Status
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide whitespace-nowrap">
      Updated By
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide whitespace-nowrap">
      Updated At
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
      Stage
    </th>

    <th className="text-left py-2 px-3 font-bold text-gray-800 text-xs uppercase tracking-wide">
      Actions
    </th>
  </tr>
</thead>                
                <tbody className="divide-y divide-gray-100">
                  {currentCourses.map((course, index) => (
                    <tr key={course._id} className={`group hover:bg-gradient-to-r hover:from-purple-25 hover:via-blue-25 hover:to-indigo-25 transition-all duration-300 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-gradient-to-r from-gray-25 to-purple-25'
                    }`}>
                      
                    <td className="py-2 px-3 w-[320px] max-w-[320px]">
                      <div
                        onClick={() => handleViewCourse(course._id, course.status)}
                        className="font-semibold text-gray-900 inline-flex items-start hover:text-purple-600 transition-all duration-200 group-hover:text-purple-700 cursor-pointer"
                      >
                        <div className="flex items-start space-x-2 w-full">

                          {/* Course Initial */}
                          <div className="w-6 h-6 flex-shrink-0 bg-gradient-to-br from-purple-100 to-blue-100 rounded-md flex items-center justify-center group-hover:scale-110 transition-transform duration-200 mt-0.5">
                            <span className="text-purple-600 font-bold text-xs">
                              {course.title.charAt(0).toUpperCase()}
                            </span>
                          </div>

                          {/* Course Name */}
                          <div className="min-w-0 flex-1">
                            <span className="block text-xs font-semibold leading-5 break-words whitespace-normal">
                              {course.title}
                            </span>

                            {/* View Details */}
                            {/* <div className="flex items-center mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <ExternalLink className="text-purple-500" size={10} />
                              <span className="ml-1 text-xs text-purple-600">
                                View Details
                              </span>
                            </div> */}
{/* View Details - Hide for Sold Out */}
{activeTab !== "Sold out" && (
  <div className="flex items-center mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
    <ExternalLink className="text-purple-500" size={10} />
    <span className="ml-1 text-xs text-purple-600">
      View Details
    </span>
  </div>
)}                            
                          </div>

                        </div>
                      </div>
                    </td>                    
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getTypeColor(course.courseType)} whitespace-nowrap`}>
                          {course.courseType === "Webinar" ? `Webinar (${(course.webinarChannel && groupNames[course.webinarChannel]) || '-'})` : "Course"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-gray-600 text-xs whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {course.createdAt ? new Date(course.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'N/A'}
                          </span>
                          <span className="text-xs text-gray-400 mt-0.5">
                            {course.createdAt ? new Date(course.createdAt).toLocaleDateString('en-US', {
                              weekday: 'long'
                            }) : ''}
                          </span>
                        </div>
                      </td>                      

                      <td className="py-2 px-3 text-gray-600 text-xs whitespace-nowrap">
                        <span className="font-medium">  {course.createdBy?.name || 'N/A'}</span>
                      </td>

                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${getStatusColor(course.status)} transition-all duration-200 hover:shadow-sm whitespace-nowrap`}>
                          {course.status}
                        </span>
                      </td>
<td className="py-2 px-3 text-gray-600 text-xs whitespace-nowrap">
  <span className="font-medium">
    {course.updatedBy?.name || 'N/A'}
  </span>
</td> 

<td className="py-2 px-3 text-gray-600 text-xs whitespace-nowrap">
  <div className="flex flex-col">
    <span className="font-medium">
      {course.updatedAt
        ? new Date(course.updatedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        : 'N/A'}
    </span>

    <span className="text-xs text-gray-400 mt-0.5">
      {course.updatedAt
        ? new Date(course.updatedAt).toLocaleDateString('en-US', {
            weekday: 'long'
          })
        : ''}
    </span>
  </div>
</td>
                      <td className="py-2 px-3 text-gray-600 text-xs whitespace-nowrap">
                        <span className="font-medium">{course.stage}</span>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex space-x-1">
                          <Link
                            to={`/courses/edit/${course._id}`}
                            className="inline-flex items-center justify-center p-1.5 text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-colors"
                            title="Edit Course"
                          >
                            <Edit size={12} />
                          </Link>
                          <button
                            className="inline-flex items-center justify-center p-1.5 text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50"
                            title="Delete Course"
                            onClick={() => handleDelete(course)}
                            disabled={deletingId === course._id}
                          >
                            {deletingId === course._id ? (
                              <div className="w-2.5 h-2.5 border-2 border-red-700 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Trash size={12} />
                            )}
                          </button>
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : (
              !loading && (
                <div className="text-center py-12">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-100 to-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">
                    {searchTerm ? 'No courses found' : activeTab !== 'all' ? `No ${activeTab} courses` : 'Create your first course'}
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6 text-xs leading-relaxed">
                    {searchTerm
                      ? `No courses match "${searchTerm}". Try adjusting your search terms or create a new course.`
                      : activeTab !== 'all'
                        ? `You don't have any ${activeTab} courses yet. Try a different tab above.`
                        : 'Start building your learning content by creating your first course. It\'s quick and easy to get started.'
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-all duration-200"
                      >
                        Clear Search
                      </button>
                    )}
                    {!searchTerm && activeTab === 'all' && (
                      <Button
                        onClick={() => navigate("/courses/create")}
                        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white inline-flex items-center justify-center px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <Plus className="mr-1" size={12} />
                        Create Your First Course
                      </Button>
                    )}
                  </div>
                </div>
              )
            )}
          </div>

        {filteredCourses.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between bg-white rounded-md border border-gray-200 px-3 py-2 shadow-sm">
            <div className="text-xs text-gray-700 mb-2 sm:mb-0">
              Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{" "}
              <span className="font-medium">{Math.min(indexOfLastItem, filteredCourses.length)}</span> of{" "}
              <span className="font-medium">{filteredCourses.length}</span> results
            </div>

            <div className="flex items-center space-x-1">
              <button
                onClick={() => paginate(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className={`p-1 rounded border text-xs font-medium transition-all duration-200 ${
                  currentPage === 1 ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50 hover:shadow-sm"
                }`}
              >
                <ChevronLeft size={12} />
              </button>

              {pageNumbers.map((pageNumber, index) => (
                <button
                  key={index}
                  onClick={() => typeof pageNumber === 'number' ? paginate(pageNumber) : null}
                  className={`px-2 py-1 rounded border text-xs font-medium min-w-[1.25rem] transition-all duration-200 ${
                    pageNumber === currentPage
                      ? "border-purple-500 bg-purple-100 text-purple-700 shadow-sm"
                      : pageNumber === '...'
                      ? "border-gray-300 bg-white text-gray-500 cursor-default"
                      : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:shadow-sm"
                  }`}
                  disabled={pageNumber === '...'}
                >
                  {pageNumber === '...' ? <MoreHorizontal size={12} /> : pageNumber}
                </button>
              ))}

              <button
                onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className={`p-1 rounded border text-xs font-medium transition-all duration-200 ${
                  currentPage === totalPages ? "text-gray-400 bg-gray-100 cursor-not-allowed" : "text-gray-700 bg-white hover:bg-gray-50 hover:shadow-sm"
                }`}
              >
                <ChevronRight size={12} />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
    </DashboardLayout>
  )
}

export default CourseList